import dayjs from "dayjs";
import { t } from "i18next";
import _ from "lodash";
import type { FieldValues } from "react-hook-form";
import {
  ref,
  type ObjectShape,
  type StringSchema,
  type TestContext,
} from "yup";

import { errors } from "../constants";
import type { TimeZoneEnum } from "../enums";
import { yupLocalized } from "../localization";
import type { DateDayjsCompatible } from "../models/common.models";
import type { ICustomField } from "../models/dynamic-forms.models";
import {
  CustomValidate,
  type ICustomValidationAdditional,
  type ICustomValidationParams,
  type IValidationYupUnit,
  type IValidationYupUnitSiblingParams,
  type TestResolver,
  type TValidationYup,
  type TValidationYupUnitMatchesParams,
} from "../models/dynamic-forms.validation.models";
import {
  additionsValueIsRequired,
  bookingPassengerCellInfoMaxRows,
  exclusivePriceAddition,
  isSiblingRequiredValidationRule,
  notBiggerTimeThanSiblingIfSiblingsNumValuesSame,
  notInThePast,
  notLowerTimeThanSiblingIfSiblingsNumValueSame,
  oneOfTwoFieldsIsRequired,
} from "./custom-yup-validation";
import { isTranslationKey } from "./localize.helpers";
import { Log } from "./log.helpers";
import { getCharacterCount } from "./string.helpers";
import { getIsShownByCondition } from "./validation.helpers";

type TStringLengthValidation = Extract<TValidationYup, "min" | "max">;

const isStringLengthValidation = (
  validation: TValidationYup,
): validation is TStringLengthValidation =>
  validation === "min" || validation === "max";

/**
 * yup's string `min`/`max` compare UTF-16 code units, while the varchar columns that seeded length rules mirror count
 * characters, so a value with an emoji at the limit fits the column but fails yup's own rule. Same test name, params and
 * message as yup's `min`/`max(limit, message)` (the locale default unless the rule gives one), counted in characters.
 * Keep in sync with the backend validation.yup.helpers.ts.
 */
export const applyStringCharacterLengthRule = ({
  validator,
  validation,
  limit,
  message,
}: {
  validator: StringSchema<string | null | undefined>;
  validation: TStringLengthValidation;
  limit: number;
  message?: string;
}): StringSchema<string | null | undefined> =>
  validator.test({
    name: validation,
    exclusive: true,
    message: message ?? yupLocalized.defaultLocale.string?.[validation],
    params: { [validation]: limit },
    skipAbsent: true,
    test: value =>
      validation === "max"
        ? getCharacterCount(value ?? "") <= limit
        : getCharacterCount(value ?? "") >= limit,
  });

export const createYupSchema = (
  fields: ICustomField[],
  fieldValues?: FieldValues,
  timezone?: TimeZoneEnum,
): ObjectShape => {
  const schema = {} as ObjectShape;
  const metadataSchema = {} as ObjectShape;
  const disabledRulesForFields: string[] = [];
  const rulesDebug: [string, IValidationYupUnit[]][] = [];
  fields.forEach(field => {
    const {
      name,
      formValidation,
      formAttributes,
      isColumn,
      formVisibleCondition,
    } = field;

    const type = formValidation?.type;
    const rules = _.cloneDeep(formValidation?.rules);

    if (rules) {
      rulesDebug.push([name, rules]);
    }

    // Not cloned: `getIsShownByCondition` only reads these values.
    const isShownByCondition = fieldValues
      ? getIsShownByCondition(fieldValues, formVisibleCondition)
      : true;

    if (!isShownByCondition) {
      disabledRulesForFields.push(name);
    }

    if (
      type &&
      rules?.length &&
      !formAttributes?.disabled &&
      isShownByCondition
    ) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let validator = (yupLocalized as any)[type]();
      const isRequired = rules.some(x => x.validation === "required");

      if (type === "number") {
        validator = validator["transform"]((value: number) =>
          isNaN(value) ? undefined : value,
        );
      } else if (type === "date") {
        validator = validator["transform"]((value: DateDayjsCompatible) =>
          !dayjs(value).isValid() ? undefined : value,
        );
      }

      rules.forEach(rule => {
        if (!rule.validation) {
          return;
        }

        if (isShownByCondition && rule.validation) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          let params: any[];

          if (rule.validation === "custom") {
            if (!rule.params?.length) {
              Log.error(t(errors.validation.absenceCustomValidationParams));
              return;
            }

            const customParams = rule.params.at(0) as ICustomValidationParams;

            const testResolver = getTestResolver(customParams);
            const message = getTestMessage(customParams);
            const validationAdditional = getValidationAdditional(customParams);

            validator = validator.test({
              name: customParams?.customValidationName,
              test: (value: unknown, context: TestContext) => {
                return testResolver?.({
                  value,
                  context,
                  errorMessage: message,
                  validationAdditional,
                  timezone,
                });
              },
            });
            return;
          }

          if (rule.validation === "matches") {
            const [regexParam, message] =
              (rule.params as TValidationYupUnitMatchesParams) || [];

            params = [
              new RegExp(regexParam),
              { message, excludeEmptyString: !isRequired },
            ];
          } else if (isSiblingRequiredValidationRule(rule)) {
            const fieldRef = (
              rule.params?.at(0) as IValidationYupUnitSiblingParams
            ).ref;
            params = [ref(fieldRef)];
          } else {
            params = rule.params ?? [];
          }

          const [limit, customMessage] = params;

          if (
            type === "string" &&
            isStringLengthValidation(rule.validation) &&
            typeof limit === "number"
          ) {
            validator = applyStringCharacterLengthRule({
              validator,
              validation: rule.validation,
              limit,
              message:
                typeof customMessage === "string" ? customMessage : undefined,
            });
            return;
          }

          if (rule.params?.length) {
            validator = validator[rule.validation](...params);
          } else {
            validator = validator[rule.validation]();
          }
        }
      });

      if (!isRequired && !rules.some(x => x.validation === "nullable")) {
        validator = validator["nullable"](true);
      }

      if (isColumn) {
        schema[name] = validator;
      } else {
        metadataSchema[name] = validator;
      }
    }
  });

  if (Object.keys(metadataSchema).length > 0) {
    schema.metadata = yupLocalized.object(metadataSchema);
  }

  if (disabledRulesForFields?.length) {
    Log.debug(
      `Dynamic forms yup schema, disabled rules for fields:`,
      disabledRulesForFields.join(", "),
    );
  }

  Log.debug("Yup generated schema rules:", rulesDebug);

  return schema;
};

const customValidationResolvers: Record<CustomValidate, TestResolver> = {
  [CustomValidate.NOT_BIGGER_TIME_THAN_SIBLING]:
    notBiggerTimeThanSiblingIfSiblingsNumValuesSame,
  [CustomValidate.NOT_LOWER_TIME_THAN_SIBLING]:
    notLowerTimeThanSiblingIfSiblingsNumValueSame,
  [CustomValidate.ONE_OF_TWO_FIELDS_IS_REQUIRED]: oneOfTwoFieldsIsRequired,
  [CustomValidate.ADDITIONS_VALUE_IS_REQUIRED]: additionsValueIsRequired,
  [CustomValidate.EXCLUSIVE_PRICE_ADDITION]: exclusivePriceAddition,
  [CustomValidate.BOOKING_PASSENGER_CELL_INFO_MAX_ROWS]:
    bookingPassengerCellInfoMaxRows,
  [CustomValidate.NOT_IN_THE_PAST]: notInThePast,
};

const getTestMessage = ({
  customValidationErrorMessage,
}: ICustomValidationParams): string => {
  return isTranslationKey(customValidationErrorMessage)
    ? t(customValidationErrorMessage)
    : (customValidationErrorMessage ?? "Custom validation error");
};

const getValidationAdditional = ({
  customValidationAdditional,
}: ICustomValidationParams): ICustomValidationAdditional => {
  return customValidationAdditional ?? {};
};

const getTestResolver = ({
  customValidationName,
}: ICustomValidationParams): TestResolver => {
  const resolver = customValidationResolvers[customValidationName];

  return resolver;
};
